import {Application} from "../Application";
import {Dict} from "../core/Reflection";
import {BookId} from "../data/BookId";
import {SlotsModel} from "../data/SlotsModel";
import {AnyStoryModel, AnyStorySceneModel, OptionId, SceneId, SlotId, StoryVO, StoryVOProvider} from "../data/StoryModel";

export class GameManager {

    public static get instance() {
        return this._instance || (this._instance = new GameManager());
    }

    get currentScene() {
        return this._story.scenes[this._currentSceneIndex];
    }

    get currentSceneIndex(): number {
        return this._currentSceneIndex;
    }

    public get visitedScenes() {
        return this._visitedScenes;
    }

    get story(): AnyStoryModel {
        return this._story;
    }

    set story(value: AnyStoryModel) {
        this._story = value;
        this.clearScenes();
        this.initStory(value);
    }

    private static _instance: GameManager;

    private _currentSceneIndex = 0;
    private _story!: AnyStoryModel;
    private readonly _slots: SlotsModel = new SlotsModel();
    private _visitedScenes: AnyStorySceneModel[] = [];
    private readonly _newBooks: Dict<BookId, boolean> = {
        [BookId.Angelou]: true,
    };

    public selectOption(optionId: OptionId | number, slotId?: SlotId): void {
        if (slotId && typeof optionId === "string") {
            this._slots.selectOption(slotId, optionId);
        } else {
            const option = this.getCurrentSceneOption(optionId);
            if (option && option.slotId) {
                this._slots.selectOption(option.slotId, option.optionId);
            }
        }
    }

    public isLastScene() {
        return this._currentSceneIndex >= this._story.scenes.length - 1;
    }

    public nextScene() {
        this.trackScene(this.currentScene);
        const option = this.getCurrentSceneSelectedOption();
        let nextScene: AnyStorySceneModel | undefined;
        if (option === undefined) {
            nextScene = this._story.scenes[this._currentSceneIndex + 1];
        } else {
            nextScene = option.nextSceneId ?
                this._story.scenes.filter((it) => it.sceneId === option.nextSceneId)[0] :
                this._story.scenes[this._currentSceneIndex + 1];
        }
        if (nextScene) {
            this._currentSceneIndex = this._story.scenes.indexOf(nextScene);
            return true;
        }
        return false;
    }

    public prevScene() {
        const prevScene = this._visitedScenes.pop();
        if (prevScene) {
            this._currentSceneIndex = this._story.scenes.indexOf(prevScene);
            return true;
        }
        return false;
    }

    public isOptionSelected(optionId: OptionId, slotId?: SlotId) {
        return this._slots.isOptionSelected(optionId, slotId);
    }

    public getSelectedOption(slotId: SlotId) {
        return this._slots.getSelectedOption(slotId);
    }

    public getOption(slotId: SlotId, optionId: OptionId) {
        for (const scene of this._story.scenes) {
            for (const option of scene.options) {
                if (option.slotId === slotId && option.optionId === optionId) {
                    return option;
                }
            }
        }
        return undefined;
    }

    public getNextOption(slotId: SlotId, optionId?: OptionId, allowEmptyOption: boolean = false) {
        const options = this.getOptions(slotId);
        if (optionId) {
            let index = options.indexOf(optionId);
            if (index >= 0) {
                index++;
                if (index >= options.length) {
                    if (allowEmptyOption) {
                        return undefined;
                    }
                    index = 0;
                }
                return options[index];
            }
        }
        return options[0];
    }

    public getOptions(slotId: SlotId) {
        for (const scene of this._story.scenes) {
            const options = scene.options.filter((it) => it.slotId === slotId);
            if (options.length > 0) {
                return options.map((it) => it.optionId);
            }
        }
        return [];
    }

    public getSceneOption(scene: AnyStorySceneModel, optionId: OptionId | number) {
        if (typeof optionId === "number") {
            return scene.options[optionId];
        }
        return scene.options.filter((it) => it.optionId === optionId)[0];
    }

    public getCurrentSceneOption(optionId: OptionId | number) {
        return this.getSceneOption(this.currentScene, optionId);
    }

    public getCurrentSceneSelectedOption() {
        return this.currentScene.options.filter((it) => this.isOptionSelected(it.optionId))[0];
    }

    public getCurrentSceneSelectedOptionIndex() {
        const option = this.getCurrentSceneSelectedOption();
        return this.currentScene.options.indexOf(option);
    }

    public getScenePromptVO(scene: AnyStorySceneModel): string | Array<string | number> | undefined {
        const vo = scene.vo;
        if (vo) {
            if (typeof vo === "object" && (vo as StoryVOProvider).getVO) {
                return (vo as StoryVOProvider).getVO();
            }
            return vo as StoryVO;
        }
        return this.replaceVOTokens(scene, this._story.promptVO, scene.voIndex);
    }

    public getCurrentScenePromptVO(): string | Array<string | number> | undefined {
        return this.getScenePromptVO(this.currentScene);
    }

    public getSceneOptionVO(scene: AnyStorySceneModel, optionIndex: number) {
        const option = this.getSceneOption(scene, optionIndex);
        if (option && option.optionVO) {
            return option.optionVO;
        }
        return this.replaceVOTokens(scene, this._story.optionVO, option ? option.voIndex : undefined, optionIndex);
    }

    public getCurrentSceneOptionVO(optionIndex: number) {
        return this.getSceneOptionVO(this.currentScene, optionIndex);
    }

    public getCurrentSceneAnswerVO(optionIndex: number) {
        const option = this.getCurrentSceneOption(optionIndex);
        if (option && option.answerVO) {
            return option.answerVO;
        }
        if (this._story.answerVO) {
            return this.replaceVOTokens(this.currentScene, this._story.answerVO, option ? option.voIndex : undefined, optionIndex);
        }
        const promptVO = this.getCurrentScenePromptVO();
        const optionVO = this.getCurrentSceneOptionVO(optionIndex);
        const answerVO: Array<string | number> = [];
        if (promptVO) {
            if (typeof promptVO === "string") {
                answerVO.push(promptVO);
            } else {
                answerVO.push(...promptVO);
            }
        }
        if (optionVO) {
            if (option.voDelay && answerVO.length > 0) {
                answerVO.push(option.voDelay);
            }
            answerVO.push(optionVO);
        }
        return answerVO.length > 0 ? answerVO : undefined;
    }

    public getCurrentSceneSfx() {
        const option = this.getCurrentSceneSelectedOption();
        if (option && option.sfx) {
            return option.sfx;
        }
        return this.currentScene.sfx;
    }

    public getScene(sceneId: SceneId) {
        return this._story.scenes.filter((it) => it.sceneId === sceneId)[0]!;
    }

    public cloneSelectedOptions() {
        return this._slots.clone();
    }

    public clearSelectedOptions() {
        this._slots.clear();
    }

    public clearScenes() {
        this._visitedScenes = [];
        this._currentSceneIndex = 0;
        this._slots.clear();
    }

    public trackAllScenes() {
        this._visitedScenes = this._story.scenes.slice();
    }

    public get completeStories() {
        return 10; // Application.instance.saveManager.readNumber("complete-stories", 0)
    }

    public set completeStories(value: number) {
        // Application.instance.saveManager.storeNumber("complete-stories", value);
    }

    public isNewBook(bookId: BookId) {
        return this._newBooks[bookId] && !Application.instance.saveManager.readBool(`story-finished-${bookId}`);
    }

    public visitBook(bookId: BookId) {
        Application.instance.saveManager.storeBool(`visited-book-${bookId}`, true);
    }

    private initStory(story: AnyStoryModel) {
        for (const scene of story.scenes) {
            for (const option of scene.options) {
                if (!option.slotId) {
                    (option as any).slotId = scene.sceneId;
                }
            }
        }
        if (story.cutouts) {
            story.cutouts.clearPolygons();
        }
    }

    private replaceVOTokens(scene: AnyStorySceneModel, pattern?: string, voIndex?: number, optionIndex?: number) {
        if (!pattern) {
            return undefined;
        }
        const sceneIndex = scene.sceneIndex || (this._currentSceneIndex + 1);
        pattern = pattern.replace("{scene}", sceneIndex.toString());
        pattern = pattern.replace("{subscene}", (scene.sceneSubIndex || "").toString());
        if (voIndex !== undefined) {
            pattern = pattern.replace("{index}", ("00" + voIndex.toString()).slice(-3));
            pattern = pattern.replace("{index2}", ("0" + voIndex.toString()).slice(-2));
        }
        if (optionIndex !== undefined) {
            pattern = pattern.replace("{option}", (optionIndex + 1).toString());
        }
        return pattern;
    }

    private trackScene(scene: AnyStorySceneModel) {
        if (this._visitedScenes.indexOf(scene) < 0) {
            this._visitedScenes.push(scene);
        }
    }
}
